import { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ChartBarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../../../utils/axios';

// Format timestamp for display
function formatTime(timestamp, interval) {
  const date = new Date(timestamp);
  if (interval === '1d') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if (interval === '6h' || interval === '1h') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

// Custom tooltip
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-gray-900 mb-2">
        {new Date(data.timestamp).toLocaleString()}
      </p>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-blue-500 rounded-full" />
          <span className="text-gray-600">Total: {data.total}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-green-500 rounded-full" />
          <span className="text-gray-600">Success: {data.success}</span>
        </div>
        {data.failure > 0 && (
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-red-500 rounded-full" />
            <span className="text-gray-600">Failure: {data.failure}</span>
          </div>
        )}
      </div>
      {data.byCategory && Object.keys(data.byCategory).length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-1">By Category:</p>
          {Object.entries(data.byCategory).map(([cat, count]) => (
            <div key={cat} className="text-xs text-gray-600">
              {cat}: {count}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ActivityTimeline({ entityType, entityId, timeRange = '24h' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTimeline();
  }, [entityType, entityId, timeRange]);

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(
        `/logs/activity/timeline/${entityType}/${encodeURIComponent(entityId)}`,
        { params: { timeRange } }
      );

      setData(response.data.data);
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setError(err.response?.data?.error?.message || 'Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <ChartBarIcon className="w-5 h-5 text-gray-400" />
          <h3 className="font-medium text-gray-900">Activity Timeline</h3>
        </div>
        <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-gray-400" />
            <h3 className="font-medium text-gray-900">Activity Timeline</h3>
          </div>
          <button onClick={fetchTimeline} className="text-blue-600 hover:text-blue-700 text-sm">
            Retry
          </button>
        </div>
        <div className="h-64 flex items-center justify-center text-gray-500">
          {error}
        </div>
      </div>
    );
  }

  const chartData = data?.points || [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ChartBarIcon className="w-5 h-5 text-blue-500" />
          <h3 className="font-medium text-gray-900">Activity Timeline</h3>
          <span className="text-xs text-gray-400">
            {data?.interval} intervals
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          {data?.summary && (
            <>
              <span className="text-gray-500">
                Peak: <span className="font-medium text-gray-900">{data.summary.peakCount}</span>
              </span>
              <span className="text-gray-500">
                Avg: <span className="font-medium text-gray-900">{data.summary.avgPerInterval}</span>
              </span>
            </>
          )}
          <button
            onClick={fetchTimeline}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {chartData.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-gray-500">
          No activity data for this time range
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorFailure" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={(val) => formatTime(val, data?.interval)}
                tick={{ fontSize: 11, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={{ stroke: '#E5E7EB' }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: 10 }}
                formatter={(value) => (
                  <span className="text-xs text-gray-600">{value}</span>
                )}
              />
              <Area
                type="monotone"
                dataKey="total"
                name="Total"
                stroke="#3B82F6"
                fill="url(#colorTotal)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="success"
                name="Success"
                stroke="#10B981"
                fill="url(#colorSuccess)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="failure"
                name="Failure"
                stroke="#EF4444"
                fill="url(#colorFailure)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
